<template>

    <div id="sidebar" :class="`sidebar-${sidebarState}`">

        <div class="sidebar-content">

            <div class="sidebar-body">

                <router-link :to="{ 'name': 'dashboard' }" class="sidebar-header">
                    <template v-if="sidebarState === 'expanded' && !loading">
                        <div v-if="agencyLogo" class="logo" :style="`background-image:url(${agencyLogo})`" :title="agencyName" />
                        <div v-else-if="agencyName" class="name" :title="agencyName">{{ agencyName }}</div>
                        <div v-else class="logo" :style="`background-image:url('/img/mgr-logo.svg')`" :title="$brand" />
                    </template>
                    <template v-if="sidebarState === 'collapsed' && !loading">
                        <div v-if="agencyIcon" class="logo" :style="`background-image:url(${agencyIcon})`" :title="agencyName" />
                        <div v-else-if="agencyName" class="name" :title="agencyName">{{ agencyName.charAt(0) }}</div>
                        <div v-else class="logo" :style="`background-image:url('/img/mgr-icon.svg')`" :title="$brand" />
                    </template>
                    <v-loader v-if="loading" :size="sidebarState === 'collapsed' ? 'sm' : 'md'" />
                </router-link>

                <div class="nav">
                    <!-- Automator -->
                    <v-the-automator v-if="sidebarState === 'expanded'" />
                    <div v-else class="nav-item" :class="{ 'text-success': project && project.is_active }" v-tooltip="{ 'content': $t('labels.automatic_review_collection'), 'placement': 'right', 'container': false, 'disabled': !tooltips }">
                        <a class="nav-link" href="#" @click="$store.commit('setModal', 'project-activate')">
                            <span><i class="icon fas fa-arrows-rotate"></i></span>
                        </a>
                    </div>
                    <!-- Items -->
                    <div class="nav-item" v-tooltip="{ 'content': $t('labels.dashboard'), 'placement': 'right', 'container': false, 'disabled': !tooltips }">
                        <router-link :to="{ 'name': 'dashboard' }" class="nav-link" :class="{ active: $route.name.startsWith('dashboard') }">
                            <span><i class="icon fas fa-house-heart"></i><span class="label">{{ $t('labels.dashboard') }}</span></span>
                        </router-link>
                    </div>
                    <div v-if="_hasSection('customers')" class="nav-item" v-tooltip="{ 'content': $t('labels.customers'), 'placement': 'right', 'container': false, 'disabled': !tooltips }">
                        <router-link :to="{ 'name': 'customers' }" class="nav-link" :class="{ active: $route.name.startsWith('customers') }">
                            <span><i class="icon fas fa-users"></i><span class="label">{{ $t('labels.customers') }}</span></span>
                        </router-link>
                    </div>
                    <div v-if="_hasSection('reviews')" class="nav-item" v-tooltip="{ 'content': $t('labels.reviews'), 'placement': 'right', 'container': false, 'disabled': !tooltips }">
                        <router-link :to="{ 'name': 'reviews' }" class="nav-link" :class="{ active: $route.name.startsWith('reviews') }">
                            <span><i class="icon fas fa-star"></i><span class="label">{{ $t('labels.reviews') }}</span></span>
                        </router-link>
                    </div>
                    <div v-if="_hasSection('review-pages')" class="nav-item" v-tooltip="{ 'content': $t('labels.review_pages'), 'placement': 'right', 'container': false, 'disabled': !tooltips }">
                        <router-link :to="{ 'name': 'forms' }" class="nav-link" :class="{ active: $route.name.startsWith('forms') }">
                            <span><i class="icon fas fa-browser"></i><span class="label">{{ $t('labels.review_pages') }}</span></span>
                        </router-link>
                    </div>
                    <div v-if="_hasSection('widgets')" class="nav-item" v-tooltip="{ 'content': $t('labels.widgets'), 'placement': 'right', 'container': false, 'disabled': !tooltips }">
                        <router-link :to="{ 'name': 'widgets' }" class="nav-link" :class="{ active: $route.name.startsWith('widgets') }">
                            <span><i class="icon fas fa-grid-2-plus"></i><span class="label">{{ $t('labels.widgets') }}</span></span>
                            <v-badge-new v-if="sidebarState === 'expanded'" date="2025-02-01" size="sm" />
                        </router-link>
                    </div>
                    <div v-if="_hasSection('messages')" class="nav-item" v-tooltip="{ 'content': $t('labels.messages'), 'placement': 'right', 'container': false, 'disabled': !tooltips }">
                        <router-link :to="{ 'name': 'messages' }" class="nav-link" :class="{ active: $route.name.startsWith('messages') }">
                            <span><i class="icon fas fa-paper-plane"></i><span class="label">{{ $t('labels.messages') }}</span></span>
                        </router-link>
                    </div>
                    <div v-if="_hasSection('analytics')" class="nav-item" v-tooltip="{ 'content': $t('labels.analytics'), 'placement': 'right', 'container': false, 'disabled': !tooltips }">
                        <router-link :to="{ 'name': 'analytics' }" class="nav-link" :class="{ active: $route.name.startsWith('analytics') }">
                            <span><i class="icon fas fa-chart-simple"></i><span class="label">{{ $t('labels.analytics') }}</span></span>
                            <v-badge-new v-if="sidebarState === 'expanded'" date="2025-06-01" size="sm" />
                        </router-link>
                    </div>
                    <div v-if="_hasSection('settings')" class="nav-item" v-tooltip="{ 'content': $t('labels.settings'), 'placement': 'right', 'container': false, 'disabled': !tooltips }">
                        <router-link :to="{ 'name': 'settings' }" class="nav-link" :class="{ active: $route.name.startsWith('settings') }">
                            <span><i class="icon fas fa-gear"></i><span class="label">{{ $t('labels.settings') }}</span></span>
                        </router-link>
                    </div>
                    <!-- External Links -->
                    <template v-if="agency">
                        <div class="nav-divider"></div>
                        <div v-for="link in agency.links" :key="link.uuid" class="nav-item" v-tooltip="{ 'content': link.label, 'placement': 'right', 'container': false, 'disabled': !tooltips }">
                            <a class="nav-link" :href="link.url" target="_blank">
                                <span><i class="icon fas fa-up-right-from-square"></i><span class="label">{{ link.label }}</span></span>
                            </a>
                        </div>
                    </template>
                </div>

            </div>

            <div class="sidebar-footer">
                <div class="nav">
                    <div class="nav-item" v-tooltip="{ 'content': $t('labels.account'), 'placement': 'right', 'container': false, 'disabled': !tooltips }">
                        <router-link :to="{ name: 'account' }" class="nav-link" :class="{ active: $route.name.startsWith('account') }">
                            <span><i class="icon fas fa-user-vneck"></i><span class="label">{{ $t('labels.account') }}</span></span>
                            <i v-if="!user.is_verified && sidebarState === 'expanded'" class="fas fa-triangle-exclamation text-danger"></i>
                        </router-link>
                    </div>
                    <div v-if="!user.is_sub_account" class="nav-item" v-tooltip="{ 'content': $t('labels.refer_and_earn'), 'placement': 'right', 'container': false, 'disabled': !tooltips }">
                        <router-link :to="{ name: 'referrals' }" class="nav-link" :class="{ active: $route.name.startsWith('referrals') }">
                            <span><i class="icon fas fa-money-bill-trend-up"></i><span class="label">{{ $t('labels.refer_and_earn') }}</span></span>
                        </router-link>
                    </div>
                    <v-sidebar-dropdown
                        v-if="!user.is_sub_account"
                        icon="fas fa-life-ring"
                        :label="$t('labels.support')"
                        :items="supportItems"
                        @change="onSupportSelect"
                    >
                        <template #append>
                            <button type="button" class="nav-dropdown-badge" data-featurebase-changelog @click.stop>
                                <span id="fb-update-badge"></span>
                            </button>
                        </template>
                    </v-sidebar-dropdown>
                    <div class="nav-item" v-tooltip="{ 'content': $t('labels.expand'), 'placement': 'right', 'container': false, 'disabled': !tooltips }">
                        <a class="nav-link" href="#" @click.prevent="$store.commit('toggleSidebarState')">
                            <span><i class="icon" :class="{ 'fas fa-chevrons-right': sidebarState === 'collapsed' , 'fas fa-chevrons-left': sidebarState === 'expanded' }"></i></span>
                        </a>
                    </div>
                </div>
            </div>

        </div>

    </div>

</template>

<style scoped lang="scss">

    @include scrollbar();

    #sidebar {
        height: 100%;
    }

    .sidebar-content {
        background: $color-white;
        border-right: 1px solid $color-rule;
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-between;
        overflow-x: hidden;
        overflow-y: auto;
        width: 100%;
    }

    .sidebar-header {
        align-items: center;
        display: flex;
        flex-shrink: 0;
        height: 60px;
        justify-content: center;
        line-height: normal;
        padding: 10px;
        position: relative;
        text-align: center;
        text-decoration: none;
        width: 100%;
        .logo {
            background-position: center;
            background-repeat: no-repeat;
            background-size: contain;
            height: 100%;
            width: 100%;
        }
        .name {
            @include font-semibold(13px);
            overflow: hidden;
            padding: 0 20px;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    .sidebar-body {
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        justify-content: space-between;
        padding: 0 10px;
    }

    .sidebar-footer {
        border-top: 1px solid $color-rule-light;
        padding: 10px;
        .nav {
            .nav-item,
            ::v-deep .nav-item {
                color: $color-muted;
                font-size: 12px;
                i.icon {
                    color: $color-muted;
                }
            }
            .nav-link,
            ::v-deep .nav-link {
                color: $color-muted;
                font-size: 12px;
            }
            ::v-deep .nav-dropdown-badge {
                appearance: none;
                background: none;
                border: none;
                line-height: 0;
                margin: 0;
                padding: 0;
                &:not(:has(#fb-update-badge:not(:empty))) {
                    display: none;
                }
            }
        }
    }

    .nav {
        display: flex;
        flex-direction: column;
        position: relative;
        .nav-divider {
            border-top: 1px solid $color-rule-light;
            margin: 10px 0;
        }
        .nav-item,
        ::v-deep .nav-item {
            @include font-semibold(13px);
            color: $color-text;
            margin-bottom: 2px;
            overflow: hidden;
            position: relative;
            text-decoration: none;
            text-overflow: ellipsis;
            user-select: none;
            white-space: nowrap;
            width: 100%;
            i.icon {
                color: $color-text;
                margin-right: 5px;
                text-align: center;
                transition: color .3s;
                width: 20px;
            }
            &.nav-item-dropdown {
                overflow: visible;
            }
            &.text-danger .nav-link,
            &.text-danger .nav-link.active,
            &.text-danger .nav-link i,
            &.text-danger .nav-link.active i {
                color: $color-danger;
            }
            &.text-success .nav-link,
            &.text-success .nav-link.active,
            &.text-success .nav-link i,
            &.text-success .nav-link.active i {
                color: $color-success;
            }
        }
        .nav-link,
        ::v-deep .nav-link {
            @include font-semibold(13px);
            align-items: center;
            background: none;
            border: none;
            border-radius: 3px;
            color: $color-text;
            display: flex;
            justify-content: space-between;
            margin: 0;
            outline: none;
            padding: 8px 10px;
            text-decoration: none;
            text-overflow: ellipsis;
            transition: background-color .3s, color .3s;
            white-space: nowrap;
            width: 100%;
            &:hover {
                background-color: $color-highlight;
                color: $color-text;
                i.icon {
                    color: $color-text;
                }
            }
            &.active {
                background-color: $color-highlight;
                color: $color-primary;
                i.icon {
                    color: $color-primary;
                }
            }
            span {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }
    }

    .sidebar-collapsed {
        .sidebar-header {
            padding: 10px 5px;
            .name {
                font-size: 20px;
                padding: 0;
            }
        }
        .sidebar-content {
            .nav-item > .nav-link .label,
            ::v-deep .nav-item > .dropdown > .nav-link .label {
                display: none;
            }
            .nav-link,
            ::v-deep .nav-link {
                align-items: center;
                display: flex;
                justify-content: center;
                padding-left: 0;
                padding-right: 0;
                position: relative;
                i {
                    width: 100%;
                }
            }
            ::v-deep .nav-dropdown-badge {
                position: absolute;
                right: 2px;
                top: 2px;
            }
        }
    }

</style>

<script>

import BadgeNew from '@cmp/BadgeNew';
import SidebarDropdown from '@cmp/SidebarDropdown';
import TheAutomator from '@cmp/TheAutomator';

export default {

    components: {
        'v-badge-new': BadgeNew,
        'v-sidebar-dropdown': SidebarDropdown,
        'v-the-automator': TheAutomator,
    },

    computed: {

        agency() {
            return this.$store.getters.agency;
        },

        agencyIcon() {
            return this.isDarkMode
                ? this.agency?.icon_dark?.link ?? this.agency?.icon?.link
                : this.agency?.icon?.link;
        },

        agencyLogo() {
            return this.isDarkMode
                ? this.agency?.logo_dark?.link ?? this.agency?.logo?.link
                : this.agency?.logo?.link;
        },

        agencyName() {
            return this.agency?.name;
        },

        isDarkMode() {
            return this.$store.getters.isDarkMode;
        },

        loading() {
            return this.$store.getters.loading;
        },

        sidebarState() {
            return this.$store.getters.sidebarState;
        },

        supportItems() {
            return [
                {
                    key: 'chat',
                    value: this.$t('labels.chat_with_us'),
                    icon: 'fas fa-messages',
                    icon_right: 'fas fa-arrow-up-right-from-square',
                },
                {
                    key: 'ai-answers',
                    value: this.$t('labels.ai_answers'),
                    icon: 'fas fa-sparkles',
                    icon_right: 'fas fa-arrow-up-right-from-square',
                },
                {
                    key: 'updates',
                    value: this.$t('labels.updates'),
                    icon: 'fas fa-bullhorn',
                    icon_right: 'fas fa-arrow-up-right-from-square',
                },
                {
                    key: 'feedback',
                    value: this.$t('labels.feedback'),
                    icon: 'fas fa-lightbulb-on',
                    icon_right: 'fas fa-arrow-up-right-from-square',
                },
                {
                    key: 'discord',
                    value: this.$t('brands.discord'),
                    icon: 'fab fa-discord',
                    icon_right: 'fas fa-arrow-up-right-from-square',
                },
                {
                    key: 'documentation',
                    value: this.$t('labels.documentation'),
                    icon: 'fas fa-book',
                    icon_right: 'fas fa-arrow-up-right-from-square',
                },
            ];
        },

        tooltips() {
            return this.sidebarState === 'collapsed';
        },

    },

    methods: {

        onSupportSelect(item) {
            if (item.key === 'chat') {
                if (typeof window.Beacon === 'function') {
                    window.Beacon('open');
                    window.Beacon('navigate', '/ask/chat/');
                }
                return;
            }
            if (item.key === 'ai-answers') {
                if (typeof window.Beacon === 'function') {
                    window.Beacon('open');
                    window.Beacon('navigate', '/ai-answers/');
                }
                return;
            }
            if (item.key === 'updates') {
                if (typeof window.Featurebase === 'function') {
                    window.Featurebase('manually_open_changelog_popup');
                }
                return;
            }
            if (item.key === 'feedback') {
                window.open(this.$feedbackUrl, '_blank');
                return;
            }
            if (item.key === 'discord') {
                window.open('https://discord.gg/9vvND2FygJ', '_blank');
                return;
            }
            if (item.key === 'documentation') {
                window.open(this.$docsUrl, '_blank');
            }
        },

    },

}

</script>
